import type { ColumnDef } from '@tanstack/react-table';
import { Card, Icon, TableListData } from 'components';
import { Button, useForm, NumberUtils, IUseFormStructureItem } from 'modules';
import React, { useCallback, useState } from 'react';
import type { GetAllReferralsEntity, ReferralsItem } from 'services/openapi';
import { DEFAULT_PAGE } from 'configs';
import ExcelJS from 'exceljs';
import saveAs from 'file-saver';
import {
  formatColumn,
  formatRowData,
  formatRowDataTotal,
} from 'configs/cell_xlsx';

import { type FilterType, getInitFilterValues, exportHeaders } from './config';
import { format } from 'date-fns';
import { useLocation } from 'react-router-dom';
import { useGetReferralTransactionQuery } from 'services/referral/queries/useGetReferralTransactionQuery';
import { useExportReferralTransactionQuery } from 'services/referral/queries/useExportReferralTransactionQuery';
import { TransactionStatusColor } from 'configs/constants';
import { useRetrySendReferralPointMutation } from 'services/referral';
import toast from 'react-hot-toast';
import { normalizedPhoneNumber } from 'utils/helpers/helpers';
import { checkValidActionRoleAdmin } from 'utils/helpers';
import { Actions } from 'configs/actions';

const ReferralTransactionsList = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const initFilterValues = getInitFilterValues(urlParams);

  const { mutateAsync: retrySendReferralPoint } =
    useRetrySendReferralPointMutation();

  const [variables, setVariables] = useState<FilterType>(initFilterValues);
  const [dataTable, setDataTable] = useState([]);
  const [lstTxResend, setLstTxResend] = useState<string[]>([]);

  const columnsDefined: ColumnDef<ReferralsItem>[] = [
    {
      accessorKey: 'transactionCode',
      header: 'Mã giao dịch',
    },
    {
      accessorKey: 'referredUserPhoneNumber',
      header: 'Người được giới thiệu',
      cell: (info) => normalizedPhoneNumber(String(info.getValue())),
    },
    {
      accessorKey: 'referrerPhoneNumber',
      header: 'Người giới thiệu',
      cell: (info) => normalizedPhoneNumber(String(info.getValue())),
    },
    {
      accessorKey: 'referrerId',
      header: 'Mã giới thiệu',
    },
    {
      accessorKey: 'point',
      header: 'Số tiền',
      cell: (info) => {
        return (
          <div className={'textInfo'}>
            {info.getValue()
              ? NumberUtils.toFormatNumber(Number(info.getValue()))
              : '0'}
          </div>
        );
      },
      // cell: (info) => (info.getValue() ? NumberUtils.toFormatNumber(Number(info.getValue())) : ''),
    },
    {
      accessorKey: 'receivePointDate',
      header: 'Thời gian',
      cell: (info) =>
        format(new Date(String(info.getValue())), 'HH:mm:ss dd/MM/yyyy'),
    },
    {
      accessorKey: 'status',
      header: 'Trạng thái',
      cell: (info) => (
        <div
          className={
            TransactionStatusColor[
              info.getValue() as keyof typeof TransactionStatusColor
            ]
          }
        >
          {String(info.getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'message',
      header: 'Lỗi',
    },
    {
      accessorKey: 'actions',
      header: 'Thao tác',
      cell: (info) => (
        <div>
          {info.row.original.status == 'Thất bại' &&
            !lstTxResend.includes(info.row.original.transactionCode) && (
              <div className='action'>
                <div
                  className='action__reject'
                  onClick={async () => {
                    try {
                      setLstTxResend((prev) => [
                        ...prev,
                        info.row.original.transactionCode,
                      ]);
                      await retrySendReferralPoint({
                        transactionCode: info.row.original.transactionCode,
                      });
                      setLstTxResend((prev) =>
                        prev.filter(
                          (tx) => tx !== info.row.original.transactionCode,
                        ),
                      );
                      toast.success('Đã gửi lại giao dịch thành công');
                      handleSubmit();
                    } catch (error: any) {
                      setLstTxResend((prev) =>
                        prev.filter(
                          (tx) => tx !== info.row.original.transactionCode,
                        ),
                      );
                    }
                  }}
                >
                  <span
                    style={{
                      color: 'var(--color-textDanger)',
                    }}
                  >
                    Thử lại
                  </span>
                </div>
              </div>
            )}
          {lstTxResend.includes(info.row.original.transactionCode) && (
            <div className='action'>
              <Icon.Loading />
            </div>
          )}
        </div>
      ),
    },
  ];

  const handleFilter = useCallback(
    (e: FilterType) => {
      setVariables({
        ...variables,
        ...e,
        refreshData: variables.refreshData + 1,
      });
    },
    [variables],
  );

  const filterOptions: IUseFormStructureItem[] = [];

  const { handleSubmit } = useForm({
    structure: filterOptions,
    onSubmit: handleFilter,
  });

  const convertVariables = (variables: FilterType) => {
    return variables;
  };

  const { isFetching: isLoadingExport, refetch } =
    useExportReferralTransactionQuery({
      page: '1',
      pageSize: String((dataTable as unknown as GetAllReferralsEntity)?.total),
    });

  const handleExportExcel = async () => {
    const allData = await refetch({
      ...convertVariables(variables),
      page: DEFAULT_PAGE,
      pageSize: String((dataTable as unknown as GetAllReferralsEntity)?.total),
    } as any);

    const dataArray = allData?.data?.data;

    const result = Array.isArray(dataArray) ? dataArray : [];

    //
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    // Columns
    const headerColumn = exportHeaders.map((header) => {
      return {
        header: header,
        key: header,
        width: 40,
      };
    });
    worksheet.columns = headerColumn;

    const firstRow = worksheet.getRow(1);
    firstRow.eachCell((cell) => formatColumn(cell));

    // Data
    result.map((item: ReferralsItem, index: number) => {
      const row = worksheet.addRow([
        index + 1,
        item.transactionCode,
        normalizedPhoneNumber(item.referredUserPhoneNumber),
        normalizedPhoneNumber(item.referrerPhoneNumber),
        item.referrerId,
        NumberUtils.toFormatNumber(item.point),
        format(new Date(String(item.receivePointDate)), 'HH:mm:ss dd/MM/yyyy'),
        item.status,
        item.message,
      ]);
      row.eachCell((cell) => formatRowData(cell));
    });

    // Total
    const row = worksheet.addRow([
      '',
      '',
      '',
      '',
      'TỔNG',
      NumberUtils.toFormatNumber(Number(allData?.data?.summary ?? 0)),
    ]);

    row.eachCell((cell, colNumber) => {
      if (colNumber === 6 || colNumber === 7) {
        formatRowDataTotal(cell);
      }
    });

    // Generate Excel and trigger file download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const now = new Date();
    saveAs(
      blob,
      `Danh_sach_Chuong_Trinh_Gioi_Thieu_${now.toLocaleTimeString('vn-VN', { hour12: false }).replace(/:/g, '_')}_${now.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '_')}.xlsx`,
    );
  };

  return (
    <>
      <Card className='container-fluid mb20'>
        {checkValidActionRoleAdmin(Actions.ManageReferralEvent_ListTransaction) && <form onSubmit={handleSubmit}>
          <div className='textCenter mt16 wrapButtonFilter'>
            <Button label={'Tìm kiếm'} type='submit' buttonType='success' />
            <Button
              className='ml15'
              onClick={handleExportExcel}
              label={'Tải về DS'}
              type='submit'
              buttonType='info'
              isLoading={isLoadingExport}
            />
          </div>
        </form>}
      </Card>

      <TableListData<GetAllReferralsEntity>
        columns={columnsDefined as ColumnDef<GetAllReferralsEntity>[]}
        useQuery={useGetReferralTransactionQuery}
        variables={variables}
        setVariables={setVariables}
        convertVariables={convertVariables}
        orderColumn
        callBackData={setDataTable}
        customTopRow={(data: any) => {
          if (data) {
            return (
              <>
                <tr>
                  <td colSpan={4}></td>
                  <td>
                    <strong>TỔNG</strong>
                  </td>
                  <td>
                    <strong>
                      {NumberUtils.toFormatNumber(Number(data?.summary ?? 0))}
                    </strong>
                  </td>
                </tr>
              </>
            );
          }
        }}
      />
    </>
  );
};

export default ReferralTransactionsList;
